import React, { useState, useEffect } from "react";
import { DateField } from "@/components/ui/DateField";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, BookOpen } from "lucide-react";
import { SubjectPicker, GradePicker } from "@/components/CurriculumPicker";
import GenerateLoader from "@/components/ui/GenerateLoader";
import AttachmentPicker from "@/components/quiz/AttachmentPicker";

export default function AILessonPlanner() {
  const [params] = useSearchParams();
  const initSubjectId = params.get("subjectId") || "";
  const [form, setForm] = useState({
    subject_id: initSubjectId,
    subject: params.get("subject") || "",
    grade_id: "",
    grade: params.get("grade") || "",
    syllabus: "",
    num_classes: 10,
    exam_date: "",
    weak_topics: "",
    learning_goals: "",
  });
  const [loading, setLoading] = useState(false);
  const [plan, setPlan] = useState(null);
  const [history, setHistory] = useState([]);
  const [instructions, setInstructions] = useState("");
  const [attachmentText, setAttachmentText] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const load = async () => {
    try {
      const url = form.subject_id ? `/ai/lesson-plan?subject_id=${form.subject_id}` : "/ai/lesson-plan";
      const { data } = await api.get(url);
      setHistory(data.lesson_plans || []);
    } catch {}
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [form.subject_id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.subject_id) return toast.error("Pick a subject");
    if (!form.grade) return toast.error("Pick a grade");
    setLoading(true); setPlan(null);
    try {
      const payload = {
        subject: form.subject,
        grade: form.grade,
        syllabus: form.syllabus,
        num_classes: Number(form.num_classes) || 10,
        exam_date: form.exam_date,
        weak_topics: form.weak_topics.split(",").map((s) => s.trim()).filter(Boolean),
        learning_goals: form.learning_goals,
        subject_id: form.subject_id,
        instructions,
        attachment_text: attachmentText,
      };
      const { data } = await api.post("/ai/lesson-plan", payload);
      setPlan(data.lesson_plan);
      toast.success("Lesson plan ready");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };

  return (
    <div className="space-y-6" data-testid="lesson-planner-page">
      <div>
        <div className="text-xs font-mono uppercase tracking-[0.2em] text-neutral-500 mb-1">/ ai lesson planner</div>
        <h1 className="font-display font-bold text-3xl tracking-tighter">Build a syllabus plan with AI</h1>
        <p className="text-sm text-neutral-600 mt-1">10 credits per plan.</p>
      </div>

      <form onSubmit={submit} className="bg-white border border-neutral-200 p-6 grid lg:grid-cols-2 gap-4">
        <SubjectPicker
          testid="lp-subject"
          className="rounded-md"
          value={form.subject_id}
          onChange={(sid, sname, gid, gname) => setForm({ ...form, subject_id: sid, subject: sname, grade_id: gid || form.grade_id, grade: gname || form.grade })}
        />
        <GradePicker
          testid="lp-grade"
          className="rounded-md"
          value={form.grade_id}
          onChange={(gid, gname) => setForm({ ...form, grade_id: gid, grade: gname })}
        />
        <input className={inp} type="number" min={1} max={50} placeholder="Number of classes" value={form.num_classes} onChange={set("num_classes")} />
        <DateField className={inp} placeholder="Exam date (dd/mm/yyyy)" value={form.exam_date} onChange={(v) => setForm((f) => ({ ...f, exam_date: v }))} testid="lesson-exam-date" />
        <input className={inp} placeholder="Weak topics (comma separated)" value={form.weak_topics} onChange={set("weak_topics")} />
        <input className={inp} placeholder="Learning goals" value={form.learning_goals} onChange={set("learning_goals")} />
        <textarea className={`${inp} lg:col-span-2`} rows={4} placeholder="Syllabus / topics to cover *" required value={form.syllabus} onChange={set("syllabus")} data-testid="lp-syllabus" />
        <textarea className={`${inp} lg:col-span-2`} rows={2} placeholder="Extra instructions for the AI (optional) — e.g. follow the Cambridge scheme, weekly revision slots" value={instructions} onChange={(e) => setInstructions(e.target.value)} data-testid="lp-instructions" />
        <div className="lg:col-span-2"><AttachmentPicker onChange={setAttachmentText} /></div>
        <button type="submit" disabled={loading} data-testid="lp-generate-btn" className="lg:col-span-2 bg-[#0A0C10] text-white font-bold py-3 inline-flex items-center justify-center gap-2 disabled:opacity-60">
          <Sparkles size={16} /> {loading ? <GenerateLoader label="Generating lesson plan" phrases={["Structuring objectives…", "Adding activities…", "Aligning to curriculum…"]} /> : "Generate lesson plan"}
        </button>
      </form>

      {plan && (
        <div className="bg-white border border-neutral-200 p-6">
          <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-2">/ generated · {plan.subject} · {plan.grade}</div>
          <div className="prose prose-sm max-w-none whitespace-pre-wrap font-mono text-[13px]">{plan.plan_content}</div>
        </div>
      )}

      <div>
        <div className="text-xs font-mono uppercase tracking-[0.18em] text-neutral-500 mb-2">/ recent plans</div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-px bg-neutral-200 border border-neutral-200">
          {history.length === 0 ? (
            <div className="bg-white p-6 text-sm text-neutral-500 col-span-full text-center">No lesson plans yet.</div>
          ) : history.map((p) => (
            <button key={p.id} onClick={() => setPlan(p)} className="bg-white p-5 text-left hover:bg-neutral-50">
              <BookOpen size={20} className="text-[#002FA7]" />
              <div className="mt-3 font-display font-semibold">{p.subject} · {p.grade}</div>
              <div className="text-xs text-neutral-500 mt-1">{p.num_classes} classes · {p.syllabus.slice(0, 70)}…</div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

const inp = "w-full bg-white border border-neutral-300 px-4 py-2.5 focus:ring-2 focus:ring-[#002FA7] focus:border-[#002FA7] outline-none";
